import React, { useState, useEffect, useCallback } from 'react';
import TimesheetForm from './Timesheet/TimesheetForm';
import TimesheetList from './Timesheet/TimesheetList';
import { useAuth } from '../contexts/AuthContext';
import { useMessage } from '../contexts/MessageContext';
import Loading from './common/Loading';
import {
  fetchTimesheets,
  deleteTimesheet,
  updateTimesheetStatus,
} from '../services/timesheet.service';

const todayStr = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const TimesheetsPage = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useMessage();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState({
    start_date: firstOfMonth(),
    end_date: todayStr(),
    status: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.status) params.status = filters.status;
      const data = await fetchTimesheets(params);
      setEntries(Array.isArray(data) ? data : []);
    } catch (err) {
      showError(err.response?.data?.message || err.message || 'Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  }, [filters, showError]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this timesheet entry?')) return;
    try {
      await deleteTimesheet(id);
      showSuccess('Timesheet deleted');
      load();
    } catch (err) {
      showError(err.response?.data?.message || err.message || 'Failed to delete timesheet');
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateTimesheetStatus(id, status);
      showSuccess(`Timesheet ${status}`);
      load();
    } catch (err) {
      showError(err.response?.data?.message || err.message || 'Failed to update status');
    }
  };

  const handleEdit = (entry) => {
    setEditing(entry);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSuccess = () => {
    setEditing(null);
    load();
  };

  const isAdmin = user?.role === 'admin';

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen">
      <h1 className="text-2xl font-bold mb-6">Timesheets</h1>

      <TimesheetForm
        entry={editing}
        onSuccess={handleSuccess}
        onCancel={() => setEditing(null)}
      />

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">From</label>
            <input
              type="date"
              name="start_date"
              value={filters.start_date}
              onChange={handleFilterChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">To</label>
            <input
              type="date"
              name="end_date"
              value={filters.end_date}
              onChange={handleFilterChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <Loading />
      ) : (
        <TimesheetList
          entries={entries}
          isAdmin={isAdmin}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onStatusChange={handleStatusChange}
        />
      )}
    </div>
  );
};

export default TimesheetsPage;
